import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { login } from '../api/client';
import { ApiClientError } from '../api/types';
import '../components/ui/ui-button';
import '../components/ui/ui-input';
import { alertStyles, cardStyles, motionStyles, boxSizingStyles } from '../styles/lit-shared';

@customElement('login-page')
export class LoginPage extends LitElement {
  static styles = [
    boxSizingStyles,
    cardStyles,
    alertStyles,
    motionStyles,
    css`
      :host {
        display: block;
      }

      .wrapper {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: calc(100dvh - var(--header-height) - 5rem);
        padding: var(--space-md) 0;
        overflow: hidden;
      }

      .orb {
        position: absolute;
        border-radius: 50%;
        filter: blur(60px);
        pointer-events: none;
        animation: pulse-glow 4s ease-in-out infinite;
      }

      .orb--violet {
        width: 280px;
        height: 280px;
        top: -40px;
        left: -60px;
        background: var(--glow-violet);
        display: none;
      }

      .orb--cyan {
        width: 220px;
        height: 220px;
        bottom: -30px;
        right: -40px;
        background: var(--glow-cyan);
        animation-delay: 1.5s;
        display: none;
      }

      .card {
        width: 100%;
        max-width: 440px;
        z-index: 1;
      }

      .card-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 3rem;
        height: 3rem;
        margin-bottom: var(--space-lg);
        border-radius: var(--radius);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        font-size: 1.375rem;
        box-shadow: 0 0 24px rgb(139 92 246 / 20%);
      }

      h1 {
        font-size: 1.625rem;
        font-weight: 700;
        letter-spacing: -0.03em;
        margin-bottom: var(--space-xs);
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .subtitle {
        color: var(--color-text-muted);
        margin-bottom: var(--space-xl);
        font-size: 0.9375rem;
        line-height: 1.65;
      }

      .subtitle strong {
        color: var(--color-primary-light);
        font-weight: 600;
      }

      form {
        display: flex;
        flex-direction: column;
        gap: var(--space-lg);
      }

      .form-error {
        margin-top: calc(var(--space-md) * -1);
      }

      .success-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 4rem;
        height: 4rem;
        margin: 0 auto var(--space-lg);
        border-radius: 50%;
        background: rgb(16 185 129 / 12%);
        border: 1px solid rgb(52 211 153 / 30%);
        font-size: 1.75rem;
        box-shadow: 0 0 32px rgb(16 185 129 / 20%);
      }

      .success-card {
        text-align: center;
      }

      .success-card h1 {
        font-size: 1.5rem;
      }

      .success-card .subtitle {
        margin-bottom: var(--space-lg);
      }

      @media (max-width: 639px) {
        .wrapper {
          padding: var(--space-md);
        }
      }
    `,
  ];

  @state() private email = '';
  @state() private loading = false;
  @state() private error = '';
  @state() private sent = false;

  private async handleSubmit(e?: Event) {
    e?.preventDefault();

    const email = this.email.trim().toLowerCase();
    if (!email) {
      this.error = 'Introduce tu email';
      return;
    }

    this.error = '';
    this.loading = true;

    try {
      await login(email);
      this.email = email;
      this.sent = true;
    } catch (err) {
      this.error =
        err instanceof ApiClientError ? err.message : 'Error al enviar el enlace';
    } finally {
      this.loading = false;
    }
  }

  private handleEmailChange(e: CustomEvent<{ value: string }>) {
    this.email = e.detail.value;
    if (this.error) this.error = '';
  }

  render() {
    if (this.sent) {
      return html`
        <div class="wrapper animate-fade-up">
          <div class="orb orb--violet"></div>
          <div class="orb orb--cyan"></div>
          <div class="card card--glow success-card">
            <div class="success-icon" aria-hidden="true">✓</div>
            <h1>Revisa tu email</h1>
            <p class="subtitle">
              Hemos enviado un enlace de acceso a
              <strong>${this.email}</strong>.
              Haz clic en el enlace para entrar al dashboard.
            </p>
            <div class="alert alert--success">
              El enlace expira en 15 minutos.
            </div>
          </div>
        </div>
      `;
    }

    return html`
      <div class="wrapper animate-fade-up">
        <div class="orb orb--violet"></div>
        <div class="orb orb--cyan"></div>
        <div class="card card--glow">
          <div class="card-icon"  aria-hidden="true">
            <span style="font-size: 44px; margin-bottom: 0.6rem;">✉</span>
          </div>
          <h1>Iniciar sesión</h1>
          <p class="subtitle">
            Introduce tu email y te enviaremos un enlace mágico para acceder
            a tu panel de contenido.
          </p>

          <form @submit=${this.handleSubmit}>
            <ui-input
              label="Email"
              type="email"
              name="email"
              placeholder="tu_mejor_email@email.com"
              autocomplete="email"
              required
              .value=${this.email}
              .invalid=${!!this.error}
              .error=${this.error}
              @value-changed=${this.handleEmailChange}
              @submit-request=${this.handleSubmit}
            ></ui-input>

            <ui-button
              type="button"
              fullwidth
              ?loading=${this.loading}
              ?disabled=${this.loading}
              @ui-click=${this.handleSubmit}
            >
              ${this.loading ? 'Enviando…' : 'Enviar enlace mágico'}
            </ui-button>
          </form>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'login-page': LoginPage;
  }
}
